// Synthetic lab sound effects using Web Audio API - no external files needed

let audioCtx: AudioContext | null = null;
let muted = false;

export function setMuted(val: boolean) { muted = val; }
export function isMuted() { return muted; }

function getCtx(): AudioContext {
  if (!audioCtx) audioCtx = new AudioContext();
  if (audioCtx.state === "suspended") audioCtx.resume();
  return audioCtx;
}

/** White noise buffer */
function noiseBuffer(ctx: AudioContext, duration: number): AudioBuffer {
  const len = ctx.sampleRate * duration;
  const buf = ctx.createBuffer(1, len, ctx.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = Math.random() * 2 - 1;
  return buf;
}

/** Bubbling sound - short random pops */
export function playBubble() {
  if (muted) return;
  const ctx = getCtx();
  const now = ctx.currentTime;

  for (let i = 0; i < 3; i++) {
    const t = now + i * 0.12 + Math.random() * 0.05;
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(300 + Math.random() * 400, t);
    osc.frequency.exponentialRampToValueAtTime(80, t + 0.08);
    gain.gain.setValueAtTime(0.15, t);
    gain.gain.exponentialRampToValueAtTime(0.001, t + 0.1);
    osc.connect(gain).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.12);
  }
}

/** Reaction fizz - sustained bubbling */
export function playReactionFizz() {
  if (muted) return;
  const ctx = getCtx();
  const now = ctx.currentTime;
  const dur = 1.5;

  // Noise component
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuffer(ctx, dur);
  const bandpass = ctx.createBiquadFilter();
  bandpass.type = "bandpass";
  bandpass.frequency.value = 2000;
  bandpass.Q.value = 0.5;
  const noiseGain = ctx.createGain();
  noiseGain.gain.setValueAtTime(0.08, now);
  noiseGain.gain.linearRampToValueAtTime(0.12, now + 0.3);
  noiseGain.gain.exponentialRampToValueAtTime(0.001, now + dur);
  noise.connect(bandpass).connect(noiseGain).connect(ctx.destination);
  noise.start(now);
  noise.stop(now + dur);

  // Pop bursts
  for (let i = 0; i < 8; i++) {
    const t = now + i * 0.18 + Math.random() * 0.08;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(200 + Math.random() * 600, t);
    osc.frequency.exponentialRampToValueAtTime(60, t + 0.06);
    g.gain.setValueAtTime(0.1, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.08);
    osc.connect(g).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.1);
  }
}

/** Flame crackling */
export function playCrackle() {
  if (muted) return;
  const ctx = getCtx();
  const now = ctx.currentTime;
  const dur = 0.8;

  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuffer(ctx, dur);
  const hp = ctx.createBiquadFilter();
  hp.type = "highpass";
  hp.frequency.value = 3000;
  const gain = ctx.createGain();
  gain.gain.setValueAtTime(0.12, now);
  gain.gain.linearRampToValueAtTime(0.06, now + dur * 0.5);
  gain.gain.exponentialRampToValueAtTime(0.001, now + dur);
  noise.connect(hp).connect(gain).connect(ctx.destination);
  noise.start(now);
  noise.stop(now + dur);

  // Crackle pops
  for (let i = 0; i < 5; i++) {
    const t = now + Math.random() * dur * 0.7;
    const osc = ctx.createOscillator();
    const g = ctx.createGain();
    osc.type = "square";
    osc.frequency.setValueAtTime(1000 + Math.random() * 2000, t);
    g.gain.setValueAtTime(0.06, t);
    g.gain.exponentialRampToValueAtTime(0.001, t + 0.03);
    osc.connect(g).connect(ctx.destination);
    osc.start(t);
    osc.stop(t + 0.04);
  }
}

/** Splash / liquid drop */
export function playSplash() {
  if (muted) return;
  const ctx = getCtx();
  const now = ctx.currentTime;

  // Impact thud
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(150, now);
  osc.frequency.exponentialRampToValueAtTime(40, now + 0.15);
  gain.gain.setValueAtTime(0.2, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.2);
  osc.connect(gain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.25);

  // Splash noise
  const noise = ctx.createBufferSource();
  noise.buffer = noiseBuffer(ctx, 0.4);
  const bp = ctx.createBiquadFilter();
  bp.type = "bandpass";
  bp.frequency.value = 4000;
  bp.Q.value = 0.3;
  const ng = ctx.createGain();
  ng.gain.setValueAtTime(0, now);
  ng.gain.linearRampToValueAtTime(0.1, now + 0.02);
  ng.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
  noise.connect(bp).connect(ng).connect(ctx.destination);
  noise.start(now);
  noise.stop(now + 0.4);
}

/** Chemical drop into beaker */
export function playDrop() {
  if (muted) return;
  const ctx = getCtx();
  const now = ctx.currentTime;

  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.type = "sine";
  osc.frequency.setValueAtTime(800, now);
  osc.frequency.exponentialRampToValueAtTime(200, now + 0.12);
  gain.gain.setValueAtTime(0.12, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
  osc.connect(gain).connect(ctx.destination);
  osc.start(now);
  osc.stop(now + 0.18);
}
